import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { regionsData } from '../lib/types'
import type { Interview } from '../lib/types'
import {
  ClipboardList, Plus, Edit3, Trash2, Archive, X, Check,
  Loader2, AlertCircle, Phone, Mail, Star,
  ArrowRight, GripVertical, Calendar, ChevronLeft, ChevronRight,
  FileText, Clock, MapPin, Briefcase, Shield, MessageSquare,
  ExternalLink, Tag, FileSpreadsheet, Download, Upload,
  Euro, CheckCircle2, User, Search
} from 'lucide-react'

// @ts-ignore
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { sendAptosFormattedEmail } from '../lib/emailUtils'

// TypeScript declarations for CDN-loaded libraries
declare const Tesseract: any
declare const pdfjsLib: any

  // Helper to convert File to Gemini inlineData format
  async function fileToGenerativePart(file: File): Promise<{ inlineData: { data: string, mimeType: string } }> {
    if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) {
      throw new Error("I file Word (.docx, .doc) non sono supportati. Per favore converti il file in PDF o usa un'immagine.");
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Data = (reader.result as string).split(',')[1];
        let mimeType = file.type || 'application/pdf';
        resolve({ inlineData: { data: base64Data, mimeType } });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function extractTextFromFile(file: File, updateProgress: (msg: string) => void): Promise<string> {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    
    // 1. Text files
    if (['txt', 'csv'].includes(ext)) {
      updateProgress('Lettura testuale diretta...')
      return await file.text()
    }
    
    // 2. DOCX Word files
    if (ext === 'docx') {
      try {
        updateProgress('Estrazione nativa Word...')
        const arrayBuffer = await file.arrayBuffer()
        const JSZip = (window as any).JSZip
        if (!JSZip) throw new Error('JSZip non caricato')
        const zip = await JSZip.loadAsync(arrayBuffer)
        const docXml = await zip.file('word/document.xml')?.async('string')
        if (docXml) {
          const text = docXml.replace(/<w:p /g, '\n<w:p ').replace(/<[^>]+>/g, ' ')
          if (text.trim().length > 50) return text
        }
      } catch (e) {
        console.warn('Errore lettura DOCX, provo OCR:', e)
      }
    }
    return ""
  }

  // Core Gemini AI extraction
  async function runGeminiExtraction(files: FileList, progressCallback: (msg: string) => void): Promise<Partial<Interview>> {
    let apiKey = localStorage.getItem('GEMINI_API_KEY');
    if (!apiKey) {
      alert("API Key Gemini mancante! Inseriscila nelle Impostazioni (Settings) per sbloccare l'OCR.");
      throw new Error("L'inserimento dell'API Key è obbligatorio per utilizzare l'A.I. Gemini.");
    }

    progressCallback("Preparazione e caricamento documenti...");
    const imageParts: any[] = [];
    let extraTextFromDocs = "";

    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.name.endsWith('.doc') || file.name.endsWith('.docx') || file.type.includes('word') || ['txt', 'csv'].includes(file.name.split('.').pop()?.toLowerCase() || '')) {
            const txt = await extractTextFromFile(file, progressCallback);
            extraTextFromDocs += `\n\n--- DOCUMENTO ALLEGATO: ${file.name} ---\n${txt}\n`;
        } else {
            imageParts.push(await fileToGenerativePart(file));
        }
    }

    progressCallback("Analisi AI Generativa in corso (Gemini 2.0)...");
    const promptText = `
    Sei un assistente AI avanzato specializzato in HR per personale sanitario in Italia.
    Analizza TUTTI i documenti forniti (CV, tesserini OPI/IPASVI, documenti di identità, codici fiscali, attestati P.IVA, polizze assicurative, attestati di laurea, contratti) ed estrai TUTTE le informazioni del candidato.
    
    ISTRUZIONI PER TIPO DOCUMENTO:
    - CARTA D'IDENTITÀ / PATENTE: estrai nome, cognome, data di nascita, indirizzo di residenza, codice fiscale se presente
    - CODICE FISCALE: estrai il codice fiscale alfanumerico di 16 caratteri
    - ATTESTATO PARTITA IVA / VISURA CAMERALE: estrai il numero di Partita IVA (11 cifre)
    - TESSERINO OPI / IPASVI / ALBO: estrai provincia dell'ordine, numero iscrizione, data iscrizione, data abilitazione
    - POLIZZA ASSICURATIVA / DICHIARAZIONE DI COPERTURA: estrai compagnia assicurativa, numero polizza, massimale in euro, data scadenza
    - CV / ATTESTATO LAUREA: estrai qualifica professionale, email, telefono, PEC, IBAN
    
    ${extraTextFromDocs}

    Regole ferree: Restituisci SOLO un oggetto JSON valido. Niente markdown come \`\`\`json. Ometti ciò che non trovi chiaramente.
    
    Chiavi (compila TUTTE quelle che riesci a trovare nei documenti):
    {
      "nome": "string (solo il nome proprio)",
      "cognome": "string (solo il cognome)",
      "codice_fiscale": "string (16 caratteri alfanumerici maiuscoli)",
      "partita_iva": "string (11 cifre, dal documento P.IVA o visura)",
      "telefono": "string (formato numerico senza spazi)",
      "email": "string",
      "pec_contratto": "string (indirizzo PEC se trovato)",
      "indirizzo": "string (domicilio: via/piazza e numero civico)",
      "residenza_contratto": "string (residenza fiscale completa con CAP e città, se diversa dal domicilio o se trovata su carta d'identità)",
      "regione": "string (regione italiana)",
      "provincia": "string (provincia italiana)",
      "qualifica": "Infermiere",
      "albo_iscrizione": "string (provincia dell'OPI/IPASVI, es. 'OPI Roma' o 'IPASVI Milano')",
      "albo_numero": "string (numero di iscrizione all'albo)",
      "data_abilitazione": "string (data abilitazione professionale, formato YYYY-MM-DD)",
      "data_iscrizione_albo": "string (data iscrizione all'albo, formato YYYY-MM-DD)",
      "polizza_compagnia": "string (nome compagnia assicurativa, es. 'Allianz', 'UnipolSai')",
      "polizza_numero": "string (numero della polizza)",
      "polizza_massimale": "string (massimale in euro, solo cifra es. '2500000')",
      "polizza_scadenza": "string (scadenza polizza, formato YYYY-MM-DD)",
      "iban": "string (formato IT...)"
    }`;

    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: promptText }, ...imageParts] }] })
      });

      if (!response.ok) {
        let eMsg = `Errore Server API (${response.status})`; try { const eData = await response.json(); eMsg += `: ${eData.error?.message || JSON.stringify(eData)}`; } catch(e){} throw new Error(eMsg);
      }

      const data = await response.json();
      let aiTextResponse = data.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
      aiTextResponse = aiTextResponse.replace(/```json/g, '').replace(/```/g, '').trim();
      return JSON.parse(aiTextResponse);
    } catch (err: any) {
      throw new Error(err.message || "Parsing fallito");
    }
  }

const STATI = [
  { key: 'in_attesa', label: 'In Attesa', emoji: '⏳', color: 'bg-yellow-50 border-yellow-300 text-yellow-700', dot: 'bg-yellow-500' },
  { key: 'colloquio_fissato', label: 'Colloquio Fissato', emoji: '📅', color: 'bg-blue-50 border-blue-300 text-blue-700', dot: 'bg-blue-500' },
  { key: 'non_idoneo', label: 'Non Idoneo', emoji: '❌', color: 'bg-red-50 border-red-300 text-red-700', dot: 'bg-red-500' },
  { key: 'esito_positivo', label: 'Esito Positivo', emoji: '🏆', color: 'bg-emerald-50 border-emerald-300 text-emerald-700', dot: 'bg-emerald-500' },
  { key: 'richiesta_documenti', label: 'Richiesta Doc.', emoji: '📄', color: 'bg-orange-50 border-orange-300 text-orange-700', dot: 'bg-orange-500' },
  { key: 'documenti_ricevuti', label: 'Doc. Ricevuti', emoji: '📬', color: 'bg-teal-50 border-teal-300 text-teal-700', dot: 'bg-teal-500' },
  { key: 'invio_contratto', label: 'Invio Contratto', emoji: '📝', color: 'bg-indigo-50 border-indigo-300 text-indigo-700', dot: 'bg-indigo-500' },
]

type ViewMode = 'kanban' | 'calendar' | 'list'

export function InterviewsManager() {
  const { canEditInterviews: isAdmin } = useAuth()
  const [interviews, setInterviews] = useState<Interview[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingInterview, setEditingInterview] = useState<Interview | null>(null)
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('kanban')
  const [calendarMonth, setCalendarMonth] = useState(new Date())
  const [newNote, setNewNote] = useState('')
  const [ocrProgress, setOcrProgress] = useState('')
  const [ocrProcessing, setOcrProcessing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showTrash, setShowTrash] = useState(false)

  // Dual scrollbar sync refs
  const kanbanRef = useRef<HTMLDivElement>(null)
  const topScrollRef = useRef<HTMLDivElement>(null)
  const scrollSyncLock = useRef(false)
  const [topScrollWidth, setTopScrollWidth] = useState(0)

  // Measure kanban scrollWidth dynamically
  useEffect(() => {
    function updateWidth() {
      if (kanbanRef.current) {
        setTopScrollWidth(kanbanRef.current.scrollWidth)
      }
    }
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    if (kanbanRef.current) observer.observe(kanbanRef.current)
    return () => observer.disconnect()
  }, [viewMode, interviews, searchQuery])

  const handleKanbanScroll = useCallback(() => {
    if (scrollSyncLock.current) return
    scrollSyncLock.current = true
    if (topScrollRef.current && kanbanRef.current) {
      topScrollRef.current.scrollLeft = kanbanRef.current.scrollLeft
    }
    requestAnimationFrame(() => { scrollSyncLock.current = false })
  }, [])

  const handleTopScroll = useCallback(() => {
    if (scrollSyncLock.current) return
    scrollSyncLock.current = true
    if (kanbanRef.current && topScrollRef.current) {
      kanbanRef.current.scrollLeft = topScrollRef.current.scrollLeft
    }
    requestAnimationFrame(() => { scrollSyncLock.current = false })
  }, [])

  // Email templates from DB
  const [emailTemplates, setEmailTemplates] = useState<Record<string, { oggetto: string; corpo: string }>>({})

  // Billing data for selected interview (matched by ID)
  const [nurseBilling, setNurseBilling] = useState<{ totaleLordo: number, totaleOre: number, totaleAccessi: number } | null>(null)
  
  useEffect(() => {
    if (selectedInterview) {
      async function fetchBilling() {
        const { data } = await supabase.from('accesses').select('ore, costo_orario, rimborso_km').eq('nurse_id', selectedInterview!.id)
        if (data) {
          const stats = data.reduce((acc: any, curr: any) => {
            acc.totaleAccessi++
            acc.totaleOre += curr.ore || 0
            acc.totaleLordo += ((curr.ore || 0) * (curr.costo_orario || 0)) + (curr.rimborso_km || 0)
            return acc
          }, { totaleLordo: 0, totaleOre: 0, totaleAccessi: 0 })
          setNurseBilling(stats)
        } else {
          setNurseBilling({ totaleLordo: 0, totaleOre: 0, totaleAccessi: 0 })
        }
      }
      fetchBilling()
    } else {
      setNurseBilling(null)
    }
  }, [selectedInterview])

  const emptyForm: Partial<Interview> = {
    nome: '', cognome: '', telefono: '', email: '', qualifica: 'Infermiere',
    regione: '', provincia: '', stato: 'in_attesa', note: '',
    data_colloquio: '', punteggio: undefined, tags: '', km_spostamento: '',
    zone_coperte: '', link_c_v: '', tipo_contratto: '', codice_fiscale: '', partita_iva: '',
    data_follow_up: '', indirizzo: '', iban: '',
    albo_iscrizione: '', albo_numero: '', data_abilitazione: '', data_iscrizione_albo: '',
    polizza_numero: '', polizza_compagnia: '', polizza_massimale: '', polizza_scadenza: '',
    residenza_contratto: '', pec_contratto: '',
  }
  const [formData, setFormData] = useState<Partial<Interview>>(emptyForm)
  const [fetchError, setFetchError] = useState('')

  useEffect(() => { fetchInterviews(); fetchEmailTemplates() }, [])

  async function fetchEmailTemplates() {
    try {
      const { data } = await supabase.from('email_templates').select('tipo, oggetto, corpo')
      if (data) {
        const map: Record<string, { oggetto: string; corpo: string }> = {}
        data.forEach((t: any) => { map[t.tipo] = { oggetto: t.oggetto, corpo: t.corpo } })
        setEmailTemplates(map)
      }
    } catch { /* ignore - use defaults */ }
  }

  // Activity log helper
  async function logActivity(entityType: string, entityId: string, action: string, details?: string) {
    try {
      await supabase.from('activity_log').insert({
        entity_type: entityType,
        entity_id: entityId,
        action,
        details: details || '',
        created_at: new Date().toISOString(),
      })
    } catch { /* non-blocking */ }
  }

  // Generic email sender using DB templates
  async function sendEmailFromTemplate(interview: Interview, templateType: string, fallbackSubject: string, fallbackBody: string, overrideTo?: string) {
    const nome = `${interview.nome} ${interview.cognome}`
    const template = emailTemplates[templateType]
    const rawSubject = template ? template.oggetto : fallbackSubject
    const rawBody = template ? template.corpo : fallbackBody
    
    const subject = rawSubject.replace(/\{\{nome\}\}/g, nome)
    let htmlFragment = rawBody.replace(/\{\{nome\}\}/g, nome)
    // Se il template è plain text (pre-Quill), convertiamo i newline
    if (!htmlFragment.includes('<p>') && !htmlFragment.includes('<br>')) {
      htmlFragment = htmlFragment.replace(/\n/g, '<br/>')
    }
    // Aggiungiamo style inline margin:0 a ogni <p> per evitare spazi extra in Outlook
    htmlFragment = htmlFragment.replace(/<p>/gi, '<p style="margin:0;padding:0;">')
    htmlFragment = htmlFragment.replace(/<p\s+class=/gi, '<p style="margin:0;padding:0;" class=')
    // Stessa cosa per gli elenchi
    htmlFragment = htmlFragment.replace(/<ul>/gi, '<ul style="margin:4px 0;padding-left:28px;">')
    htmlFragment = htmlFragment.replace(/<ol>/gi, '<ol style="margin:4px 0;padding-left:28px;">')
    htmlFragment = htmlFragment.replace(/<li>/gi, '<li style="margin:0;padding:0;">')

    // Template HTML compatto per clipboard — margini azzerati ovunque
    const htmlBody = `<div style="font-family:'Aptos',Calibri,Arial,sans-serif;font-size:12pt;color:#333333;line-height:1.5;">${htmlFragment}</div>`

    const toEmail = overrideTo || interview.email || ''
    const ccEmail = localStorage.getItem('quicare_cc_emails') || ''

    // Se siamo nell'app desktop, usiamo l'espansione Outlook diretta per mantenere l'HTML!
    if (window.electronAPI && window.electronAPI.createOutlookEmail) {
      try {
        const res = await window.electronAPI.createOutlookEmail({
          to: toEmail,
          cc: ccEmail,
          subject: subject,
          htmlBody: htmlBody
        })
        if (!res.success) {
          console.warn('Errore desktop email: Fallback a mailto', res.message)
          fallbackMailto()
        }
      } catch (e) {
        console.warn('Eccezione desktop email: Fallback a mailto', e)
        fallbackMailto()
      }
    } else {
      fallbackMailto()
    }

    async function fallbackMailto() {
      // Strategia: copiamo l'HTML formattato negli appunti e apriamo Outlook con mailto
      // L'utente poi fa Ctrl+V per incollare il corpo formattato
      try {
        // Copiamo HTML negli appunti (preserva grassetto, corsivo, elenchi puntati)
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlBody], { type: 'text/html' }),
          'text/plain': new Blob([htmlToPlainText(htmlFragment)], { type: 'text/plain' }),
        })
        await navigator.clipboard.write([clipboardItem])
      } catch {
        // Fallback: copia solo testo
        try { await navigator.clipboard.writeText(htmlToPlainText(htmlFragment)) } catch {}
      }

      // Apriamo mailto con destinatario, oggetto e body di fallback in plaintext
      const subjectEncoded = encodeURIComponent(subject)
      const bodyEncoded = encodeURIComponent(htmlToPlainText(htmlFragment))
      const ccPart = ccEmail ? `cc=${encodeURIComponent(ccEmail)}&` : ''
      const mailtoUrl = overrideTo 
        ? `mailto:${toEmail}?subject=${subjectEncoded}&body=${bodyEncoded}`
        : `mailto:${toEmail}?${ccPart}subject=${subjectEncoded}&body=${bodyEncoded}`
      window.open(mailtoUrl, '_self')

      // Avvisiamo l'utente
      setSuccess('📋 Corpo email copiato! Premi Ctrl+V nel corpo dell\'email in Outlook per incollare il testo formattato.')
    }

    function htmlToPlainText(html: string): string {
      let text = html
      text = text.replace(/<li>/gi, '\r\n  • ')
      text = text.replace(/<\/li>/gi, '')
      text = text.replace(/<ul[^>]*>/gi, '\r\n')
      text = text.replace(/<\/ul>/gi, '\r\n')
      text = text.replace(/<ol[^>]*>/gi, '\r\n')
      text = text.replace(/<\/ol>/gi, '\r\n')
      text = text.replace(/<br\s*[/]?>/gi, '\r\n')
      text = text.replace(/<\/p>/gi, '\r\n')
      text = text.replace(/<p[^>]*>/gi, '')
      text = text.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '*$1*')
      text = text.replace(/<b[^>]*>(.*?)<\/b>/gi, '*$1*')
      text = text.replace(/<em[^>]*>(.*?)<\/em>/gi, '_$1_')
      text = text.replace(/<i[^>]*>(.*?)<\/i>/gi, '_$1_')
      text = text.replace(/<h[1-3][^>]*>(.*?)<\/h[1-3]>/gi, '\r\n$1\r\n')
      text = text.replace(/<[^>]+>/g, '')
      text = text.replace(/&nbsp;/g, ' ')
      text = text.replace(/&amp;/g, '&')
      text = text.replace(/&lt;/g, '<')
      text = text.replace(/&gt;/g, '>')
      text = text.replace(/(\r\n){3,}/g, '\r\n\r\n')
      return text.trim()
    }

    logActivity('interview', interview.id, `email_${templateType}`, `Email ${templateType} inviata a ${overrideTo || nome}`)
  }

  async function fetchInterviews() {
    setIsLoading(true)
    setFetchError('')
    const { data, error } = await supabase
      .from('interviews')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching interviews:', error)
      setFetchError(error.message || 'Errore nel caricamento dei colloqui')
    } else {
      const mappedData = data.map((iv: any) => {
        let s = iv.stato || 'in_attesa'
        if (['da_contattare', 'contattato'].includes(s)) s = 'in_attesa'
        if (s === 'da_fare') s = 'colloquio_fissato'
        if (['colloquio_fatto', 'andato_bene'].includes(s)) s = 'esito_positivo'
        if (s === 'escluso') s = 'non_idoneo'
        if (['idoneo', 'contratto_firmato'].includes(s)) s = 'invio_contratto'
        return { ...iv, stato: s }
      }) as Interview[]
      setInterviews(mappedData)
    }
    setIsLoading(false)
  }

  // Filter by search
  const filteredInterviews = useMemo(() => {
    const list = showTrash ? interviews : interviews.filter(i => !i.cestino)
    if (!searchQuery.trim()) return list
    const q = searchQuery.toLowerCase()
    return list.filter(i =>
      (i.nome || '').toLowerCase().includes(q) ||
      (i.cognome || '').toLowerCase().includes(q) ||
      (i.qualifica || '').toLowerCase().includes(q) ||
      (i.regione || '').toLowerCase().includes(q) ||
      (i.provincia || '').toLowerCase().includes(q) ||
      (i.telefono || '').toLowerCase().includes(q) ||
      (i.email || '').toLowerCase().includes(q) ||
      (i.zone_coperte || '').toLowerCase().includes(q)
    )
  }, [interviews, searchQuery])

  // Group by stato
  const grouped = useMemo(() => {
    const map: Record<string, Interview[]> = {}
    STATI.forEach(s => { map[s.key] = [] })
    filteredInterviews.forEach(i => {
      const key = i.stato || 'da_contattare'
      if (!map[key]) map[key] = []
      map[key].push(i)
    })
    return map
  }, [filteredInterviews])

  // Calendar data
  const calendarInterviews = useMemo(() => {
    const month = calendarMonth.getMonth()
    const year = calendarMonth.getFullYear()
    return filteredInterviews.filter(i => {
      if (!i.data_colloquio) return false
      const d = new Date(i.data_colloquio)
      return d.getMonth() === month && d.getFullYear() === year
    })
  }, [filteredInterviews, calendarMonth])

async function fileToImageUrl(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase()
  if (ext === 'pdf') {
    const pdfjsLib = (window as any).pdfjsLib
    const arrayBuffer = await file.arrayBuffer()
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
    const page = await pdf.getPage(1)
    const scale = 3.5 // high-res for better OCR
    const viewport = page.getViewport({ scale })
    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height
    const ctx = canvas.getContext('2d')!
    await page.render({ canvasContext: ctx, viewport }).promise
    return new Promise<string>((resolve) => {
      canvas.toBlob((blob) => {
        resolve(URL.createObjectURL(blob!))
      }, 'image/png')
    })
  }
  return URL.createObjectURL(file)
}

// Advanced File Text Extractor for CVs and Documents
async function extractTextFromFile(file: File, updateProgress: (msg: string) => void): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || ''
  if (['txt', 'csv'].includes(ext)) {
    updateProgress('Lettura testuale diretta...')
    return await file.text()
  }
  if (ext === 'docx') {
    try {
      updateProgress('Estrazione nativa Word...')
      const arrayBuffer = await file.arrayBuffer()
      const JSZip = (window as any).JSZip
      if (!JSZip) throw new Error('JSZip non caricato')
      const zip = await JSZip.loadAsync(arrayBuffer)
      const docXml = await zip.file('word/document.xml')?.async('string')
      if (docXml) {
        const text = docXml.replace(/<w:p /g, '\n<w:p ').replace(/<[^>]+>/g, ' ')
        if (text.trim().length > 50) return text
      }
    } catch (e) {
      console.warn('Errore lettura DOCX, provo OCR:', e)
    }
  }
  if (ext === 'pdf') {
    try {
      updateProgress('Estrazione testuale PDF...')
      const pdfjsLib = (window as any).pdfjsLib
      if (!pdfjsLib) throw new Error('PDF.js non caricato')
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      let fullText = ''
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const textContent = await page.getTextContent()
        const strings = textContent.items.map((item: any) => item.str)
        fullText += strings.join(' ') + '\n'
      }
      if (fullText.trim().length > 100) return fullText
      updateProgress('PDF scansionato: passo a OCR Visivo...')
    } catch (e) {
      console.warn('Errore estrazione PDF nativa:', e)
    }
  }
  updateProgress('Inizializzazione Motore Visivo (OCR)...')
  if (typeof Tesseract === 'undefined') throw new Error('Libreria OCR non caricata')
  const imageUrl = await fileToImageUrl(file)
  const worker = await Tesseract.createWorker('ita', 1, {
    logger: (m: any) => { if (m.status === 'recognizing text') updateProgress(`Analisi Immagine: ${Math.round((m.progress || 0) * 100)}%`) },
  })
  const { data: { text } } = await worker.recognize(imageUrl)
  URL.revokeObjectURL(imageUrl)
  await worker.terminate()
  return text
}

  function handleOpenForm(interview?: Interview) {
    if (interview) {
      setEditingInterview(interview)
      setFormData({ ...interview })
    } else {
      setEditingInterview(null)
      setFormData(emptyForm)
    }
    setShowForm(true)
  }

  async function handleOCRForm(files: FileList) {
    if (files.length === 0) return
    setOcrProcessing(true)
    
    try {
      const extracted = await runGeminiExtraction(files, setOcrProgress);

      const updates: Partial<Interview> = {};
      const compilati: string[] = [];
      const ignorati: string[] = [];
      const fieldLabels: Record<string, string> = {
        nome: 'Nome', cognome: 'Cognome', telefono: 'Telefono', email: 'Email',
        codice_fiscale: 'Codice Fiscale', partita_iva: 'P.IVA', qualifica: 'Qualifica',
        indirizzo: 'Indirizzo', residenza_contratto: 'Residenza', pec_contratto: 'PEC',
        albo_iscrizione: 'Albo OPI', albo_numero: 'N° Albo', data_abilitazione: 'Data Abilitazione',
        data_iscrizione_albo: 'Data Iscrizione', polizza_compagnia: 'Compagnia Assicurativa',
        polizza_numero: 'N° Polizza', polizza_massimale: 'Massimale', polizza_scadenza: 'Scadenza Polizza',
        iban: 'IBAN', regione: 'Regione', provincia: 'Provincia',
      };

      for (const [key, value] of Object.entries(extracted)) {
        if (value && typeof value === 'string' && value.trim() !== '') {
          const label = fieldLabels[key] || key;
          if (!formData[key as keyof Interview] || formData[key as keyof Interview] === 'Infermiere') {
            updates[key as keyof Interview] = value as any;
            compilati.push(`${label}: ${value.length > 30 ? value.substring(0, 30) + '...' : value}`);
          } else {
            ignorati.push(`${label} (già presente)`);
          }
        }
      }

      if (compilati.length > 0) {
        setFormData((prev: any) => ({ ...prev, ...updates }));
        let msg = `✅ AI: ${compilati.length} campi compilati:\n• ${compilati.join('\n• ')}`;
        if (ignorati.length > 0) msg += `\n\n⏭️ ${ignorati.length} campi ignorati:\n• ${ignorati.join('\n• ')}`;
        setSuccess(msg);
      } else {
        const msg = ignorati.length > 0
          ? `AI completata. Nessun nuovo dato aggiunto.\n⏭️ ${ignorati.length} campi ignorati (già presenti):\n• ${ignorati.join('\n• ')}`
          : 'AI completata. Nessun dato utile trovato nei documenti caricati.';
        setSuccess(msg);
      }
    } catch (err: any) {
      setError('Errore AI: ' + (err.message || 'sconosciuto'));
    } finally {
      setOcrProcessing(false);
      setOcrProgress('');
    }
  }

  async function handleSave() {
    if (!formData.nome || !formData.cognome) {
      setError('Nome e cognome sono obbligatori')
      return
    }
    setSaving(true)
    setError('')

    try {
      if (editingInterview) {
        const { error: err } = await supabase
          .from('interviews')
          .update(formData)
          .eq('id', editingInterview.id)
        if (err) throw err
        setSuccess('Colloquio aggiornato!')
      } else {
        const { error: err } = await supabase
          .from('interviews')
          .insert(formData)
        if (err) throw err
        setSuccess('Colloquio creato!')
      }
      setShowForm(false)
      fetchInterviews()
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Eliminare il colloquio di "${name}"?`)) return
    const { error: err } = await supabase.from('interviews').delete().eq('id', id)
    if (err) {
      setError('Errore: ' + err.message)
    } else {
      setSuccess('Eliminato')
      if (selectedInterview?.id === id) setSelectedInterview(null)
      fetchInterviews()
    }
  }

  async function handleMoveStatus(interview: Interview, newStatus: string) {
    const statoObj = STATI.find(s => s.key === newStatus)
    const now = new Date().toISOString()
    
    // Update step history
    const currentHistory = (interview.step_history as any[]) || []
    const newHistory = [...currentHistory, {
      stepId: newStatus,
      stepLabel: statoObj?.label || newStatus,
      timestamp: now
    }]

    const { error: err } = await supabase
      .from('interviews')
      .update({ stato: newStatus, step_history: newHistory })
      .eq('id', interview.id)
    if (err) {
      setError('Errore: ' + err.message)
    } else {
      logActivity('interview', interview.id, 'status_change', `${interview.cognome} ${interview.nome}: ${interview.stato} → ${newStatus}`)
      fetchInterviews()
    }
  }

  // Archive transfer: move interview to nurses table
  async function handleArchiveTransfer(interview: Interview) {
    if (!confirm(`Spostare ${interview.cognome} ${interview.nome} nell'archivio infermieri?\n\nTutti i dati, note e storico verranno preservati.`)) return
    
    try {
      const nurseData = {
        nome: interview.nome,
        cognome: interview.cognome,
        codice_fiscale: interview.codice_fiscale || null,
        partita_iva: interview.partita_iva || null,
        telefono: interview.telefono || null,
        email: interview.email || null,
        regione: interview.regione || '',
        provincia: interview.provincia || '',
        indirizzo: interview.indirizzo || null,
        qualifica: interview.qualifica || null,
        contratto: interview.tipo_contratto || null,
        contratto_firmato: true,
        pec: interview.pec_contratto || null,
        residenza: interview.residenza_contratto || null,
        opi_provincia: interview.albo_iscrizione || null,
        opi_numero: interview.albo_numero || null,
        assicurazione: interview.polizza_compagnia || null,
        assicurazione_scadenza: interview.polizza_scadenza || null,
        polizza_massimale: interview.polizza_massimale || null,
        iban: interview.iban || null,
        data_iscrizione_albo: interview.data_iscrizione_albo || null,
        data_abilitazione: interview.data_abilitazione || null,
        residenza_contratto: interview.residenza_contratto || null,
        note: interview.note || null,
        note_history: interview.note_history || null,
        step_history: interview.step_history || null,
        interview_notes: ((interview.note_history as any[]) || []).map((n: any) => `[${new Date(n.date).toLocaleDateString('it-IT')}] ${n.text}`).join('\n'),
        data_colloquio: interview.data_colloquio || null,
        zone_coperte: interview.zone_coperte || null,
        km_spostamento: interview.km_spostamento || null,
        tipo_contratto: interview.tipo_contratto || null,
        tags: interview.tags || null,
        link_c_v: interview.link_c_v || null,
        source_interview_id: interview.id,
      }

      const { error: insertErr } = await supabase.from('nurses').insert(nurseData)
      if (insertErr) throw new Error('Errore creazione archivio: ' + insertErr.message)

      // Elimina il colloquio originario in quanto convertito in infermiere effettivo
      await supabase.from('interviews').delete().eq('id', interview.id)

      setSuccess(`${interview.cognome} ${interview.nome} spostato in archivio!`)
      setSelectedInterview(null)
      fetchInterviews()
    } catch (err: any) {
      setError(err.message || 'Errore nel trasferimento')
    }
  }

  // Send document request email — Partita IVA
  function handleSendDocRequestPIVA(interview: Interview) {
    sendEmailFromTemplate(interview, 'richiesta_documenti_piva',
      `Richiesta Documenti Partita IVA - {{nome}}`,
      `Gentile {{nome}},\n\ncon riferimento al colloquio sostenuto, Le chiediamo cortesemente di inviarci i seguenti documenti necessari per l'attivazione della collaborazione in regime di Partita IVA:\n\n- Copia documento d'identita in corso di validita\n- Codice Fiscale\n- Visura camerale o certificato attribuzione P.IVA\n- Certificato di iscrizione all'Albo professionale (OPI)\n- Polizza assicurativa professionale in corso di validita\n- Curriculum Vitae aggiornato\n- DURC (Documento Unico di Regolarita Contributiva)\n\nLa preghiamo di inviare il tutto rispondendo a questa email.\n\nCordiali saluti,\nQuiCare Srl`
    )
    handleMoveStatus(interview, 'richiesta_documenti')
  }

  // Send document request email — Ritenuta d'Acconto
  function handleSendDocRequestRitenuta(interview: Interview) {
    sendEmailFromTemplate(interview, 'richiesta_documenti_ritenuta',
      `Richiesta Documenti Ritenuta d'Acconto - {{nome}}`,
      `Gentile {{nome}},\n\ncon riferimento al colloquio sostenuto, Le chiediamo cortesemente di inviarci i seguenti documenti necessari per l'attivazione della collaborazione in regime di Ritenuta d'Acconto:\n\n- Copia documento d'identita in corso di validita\n- Codice Fiscale\n- Certificato di iscrizione all'Albo professionale (OPI)\n- Polizza assicurativa professionale in corso di validita\n- Curriculum Vitae aggiornato\n- Autocertificazione di prestazione occasionale\n\nLa preghiamo di inviare il tutto rispondendo a questa email.\n\nCordiali saluti,\nQuiCare Srl`
    )
    handleMoveStatus(interview, 'richiesta_documenti')
  }

  // Automatic gender recognition from italian first names
  function getGender(name: string = ''): 'M' | 'F' {
    const cleanName = name.trim().split(' ')[0].toLowerCase()
    if (!cleanName) return 'M'
    
    // Female names not ending in 'a'
    const femaleExceptions = ['alice', 'beatrice', 'irene', 'matilde', 'carmen', 'noemi', 'ester', 'rachele', 'miriam', 'adele', 'agnese', 'cloe', 'daphne', 'eunice', 'ines', 'iris', 'ruth']
    if (femaleExceptions.includes(cleanName)) return 'F'
    
    // Male names ending in 'a'
    const maleExceptions = ['andrea', 'luca', 'mattia', 'nicola', 'elia', 'battista', 'evangelista', 'tobia', 'leonida', 'giona', 'michea', 'barnaba']
    if (maleExceptions.includes(cleanName)) return 'M'
    
    const lastChar = cleanName.slice(-1)
    if (lastChar === 'a') return 'F'
    return 'M' 
  }

  function handleSollecitoDocumenti(interview: Interview) {
    const genere = getGender(interview.nome)
    const interes = genere === 'F' ? 'a' : 'o'
    const nomeCognome = `${interview.nome || ''} ${interview.cognome || ''}`.trim() || 'Professionista'
    
    sendEmailFromTemplate(
      interview,
      'sollecito_documenti',
      `Sollecito richiesta documentazione stipula contratto - QuiCare`,
      `Gentile ${nomeCognome},\n\nFacendo seguito alla nostra precedente comunicazione e non avendo ancora ricevuto riscontro, Le scriviamo per richiedere nuovamente i documenti necessari per l'attivazione della collaborazione.\n\nQualora non fosse più interessat${interes} a procedere, La preghiamo cortesemente di farcelo sapere rispondendo a questa e-mail, così da poter aggiornare le nostre anagrafiche ed evitare ulteriori solleciti.\n\nRimaniamo a disposizione per qualsiasi dubbio o chiarimento.\n\nCordiali saluti,`
    )
    
    logActivity('interview', interview.id, 'email_sollecito', `Inviato sollecito documenti a ${nomeCognome}`)
  }

  function handleSendContractToCarlo(interview: Interview) {
    const nomeCognome = `${interview.cognome || ''} ${interview.nome || ''}`.trim()
    const tipoFormat = interview.tipo_contratto === 'ritenuta' ? "Ritenuta d'Acconto" : (interview.tipo_contratto === 'piva' ? "Partita IVA" : "Non specificato")
    
    sendEmailFromTemplate(
      interview,
      'invio_contratto_carlo',
      `Invio contratto - ${nomeCognome} - ${tipoFormat}`,
      `Ciao Carlo,\n\nTi invio un nuovo contratto in ${tipoFormat}\n\nIn allegato trovi anche i documenti ricevuti.\n\nSaluti`,
      'carlo.vergara@quicare.com'
    )
    
    logActivity('interview', interview.id, 'email_contract_carlo', `Contratto inviato a Carlo per ${nomeCognome}`)
    handleMoveStatus(interview, 'invio_contratto')
  }

  // Generate contract DOCX (Word) from template using docxtemplater
  async function handleGenerateContract(interview: Interview, contractType?: 'piva' | 'ritenuta') {
    const PizZipLib = (window as any).PizZip
    const Docxtemplater = (window as any).docxtemplater
    
    if (!PizZipLib || !Docxtemplater) { 
      setError('Librerie docxtemplater non caricate. Assicurati di essere online e ricarica la pagina.')
      return 
    }

    const tipo = contractType || (interview.tipo_contratto?.toLowerCase().includes('ritenuta') ? 'ritenuta' : 'piva')
    const templateUrl = tipo === 'ritenuta' ? '/template-ritenuta.docx' : '/template-piva.docx'
    const nome = `${interview.nome || ''} ${interview.cognome || ''}`.trim()

    setSuccess(`⏳ Scaricamento template ${tipo === 'piva' ? 'P.IVA' : "Ritenuta d'Acconto"}...`)

    try {
      const response = await fetch(templateUrl + '?t=' + new Date().getTime()) // Bypass cache
      if (!response.ok) throw new Error(`Template non trovato: ${templateUrl}`)
      const templateBlob = await response.arrayBuffer()

      const zip = new PizZipLib(templateBlob)

      // We no longer attempt to manually repair fragmented tag runs via aggressive Regex,
      // as it causes severe corruption resulting in missing contract pieces.
      // Docxtemplater handles standard tags itself. If tags fail to map, the user must cleanly re-type them in Word without formatting breaks.
      const docXmlFile = zip.file('word/document.xml')
      if (docXmlFile) {
        let docXml = docXmlFile.asText()
        // Solo rimozione sicura degli errori di spelling invisibili che spezzano i tag
        docXml = docXml.replace(/<w:proofErr[^>]*\/>/g, '')
        zip.file('word/document.xml', docXml)
      }

      const coreXmlFile = zip.file('docProps/core.xml')
      if (coreXmlFile) {
        let coreXml = coreXmlFile.asText()
        coreXml = coreXml.replace(/<dc:creator>.*?<\/dc:creator>/, '<dc:creator>Giuseppe Di Maso | QuiCare</dc:creator>')
        coreXml = coreXml.replace(/<cp:lastModifiedBy>.*?<\/cp:lastModifiedBy>/, '<cp:lastModifiedBy>Giuseppe Di Maso | QuiCare</cp:lastModifiedBy>')
        zip.file('docProps/core.xml', coreXml)
      }

      const settingsFile = zip.file('word/settings.xml')
      if (settingsFile) {
        let settingsXml = settingsFile.asText()
        if (!settingsXml.includes('w:trackChanges')) {
          settingsXml = settingsXml.replace('</w:settings>', '<w:trackChanges/><w:revisionView w:markup="1"/></w:settings>')
        }
        zip.file('word/settings.xml', settingsXml)
      }

      const dataIscrizione = interview.data_iscrizione_albo || interview.data_abilitazione || ''
      const dataFormattata = dataIscrizione ? new Date(dataIscrizione).toLocaleDateString('it-IT') : ''
      const dataAbilitazione = interview.data_abilitazione ? new Date(interview.data_abilitazione).toLocaleDateString('it-IT') : ''
      const polizzaScadenza = interview.polizza_scadenza ? new Date(interview.polizza_scadenza).toLocaleDateString('it-IT') : ''

      const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true })
      
      doc.render({
        titolo_genere: getGender(interview.nome) === 'F' ? 'Dott.ssa' : 'Dott.',
        nome_cognome: nome,
        cf: interview.codice_fiscale || '________________',
        'p.iva': interview.partita_iva || '________________',
        residenza: interview.residenza_contratto || interview.indirizzo || '________________',
        pec: interview.pec_contratto || '________________',
        data_abilitazione: dataAbilitazione || dataFormattata || '________________',
        albo_iscrizione: interview.albo_iscrizione || '________________',
        albo_numero: interview.albo_numero || '________',
        data_iscrizione: dataFormattata || '________________',
        polizza_numero: interview.polizza_numero || '________________',
        polizza_compagnia: interview.polizza_compagnia || '________________',
        polizza_massimale: interview.polizza_massimale || '________________',
        polizza_scadenza: polizzaScadenza || '________________',
        iban: (interview as any).iban || '________________________________',
        professionista: interview.pec_contratto || '________________'
      })

      const blob = doc.getZip().generate({ 
        type: 'blob', 
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        compression: "DEFLATE",
        compressionOptions: {
            level: 9
        }
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const today = new Date().toLocaleDateString('it-IT').replace(/\//g, '-')
      const tipoLabel = tipo === 'piva' ? 'Partita_IVA' : 'Ritenuta_Acconto'
      const cleanCognome = (interview.cognome || '').trim()
      const cleanNome = (interview.nome || '').trim()
      a.download = `Contratto di consulenza infermieristica - ${cleanCognome} ${cleanNome}.docx`
      a.click()
      URL.revokeObjectURL(url)
      
      setSuccess(`✅ Contratto in REVISIONE generato! Aprilo con Word.`)
      logActivity('interview', interview.id, 'generate_contract', `Contratto ${tipoLabel} generato per ${nome}`)
    } catch (err: any) {
      console.error('Contract generation error:', err)
      setError('Errore generazione contratto: ' + (err.message || 'sconosciuto'))
    }
  }

  // Open FNOPI to verify OPI registration
  function handleVerifyOPI(interview: Interview) {
    const fnopiUrl = new URL('https://www.fnopi.it/gli-ordini-provinciali/ricerca-albo/')
    // Passa solo il cognome alla ricerca (il nome non serve da solo)
    if (interview.cognome) fnopiUrl.searchParams.append('ra_surname', interview.cognome)
    // Se conosciamo la provincia, seleziona l'ordine corrispondente
    if (interview.provincia) fnopiUrl.searchParams.append('ra_province', interview.provincia)
    fnopiUrl.searchParams.append('ra_do_find', '1')
    
    window.open(fnopiUrl.toString(), '_blank')
    logActivity('interview', interview.id, 'verify_opi', `Verifica OPI per ${interview.cognome} ${interview.nome}`)
  }


  async function handleAddNote(interview: Interview) {
    if (!newNote.trim()) return
    const statoObj = STATI.find(s => s.key === interview.stato)
    const currentHistory = (interview.note_history as any[]) || []
    const updatedHistory = [...currentHistory, {
      text: newNote.trim(),
      date: new Date().toISOString(),
      stepId: interview.stato,
      stepLabel: statoObj?.label || interview.stato
    }]

    const { error: err } = await supabase
      .from('interviews')
      .update({ note_history: updatedHistory })
      .eq('id', interview.id)
    
    if (err) {
      setError('Errore: ' + err.message)
    } else {
      setNewNote('')
      fetchInterviews()
      // Update selected interview too
      setSelectedInterview(prev => prev ? { ...prev, note_history: updatedHistory } : null)
    }
  }

  // === CV UPLOAD + OCR AUTO-FILL (MULTI-FILE) ===
  async function handleCVUpload(interview: Interview, files: FileList) {
    setOcrProcessing(true)
    setOcrProgress(`Inizializzazione AI per ${files.length} documento/i...`)

    try {
      // 1. Estrazione Intelligenza Artificiale
      setOcrProgress(`Lettura ${files.length} documenti...`)
      const extracted = await runGeminiExtraction(files, setOcrProgress);
      
      const updates: Partial<Interview> = {}
      const compilati: string[] = []
      const ignorati: string[] = []
      const fieldLabels: Record<string, string> = {
        nome: 'Nome', cognome: 'Cognome', telefono: 'Telefono', email: 'Email',
        codice_fiscale: 'Codice Fiscale', partita_iva: 'P.IVA', qualifica: 'Qualifica',
        indirizzo: 'Indirizzo', residenza_contratto: 'Residenza', pec_contratto: 'PEC',
        albo_iscrizione: 'Albo OPI', albo_numero: 'N° Albo', data_abilitazione: 'Data Abilitazione',
        data_iscrizione_albo: 'Data Iscrizione', polizza_compagnia: 'Compagnia Assicurativa',
        polizza_numero: 'N° Polizza', polizza_massimale: 'Massimale', polizza_scadenza: 'Scadenza Polizza',
        iban: 'IBAN', regione: 'Regione', provincia: 'Provincia',
      }
      for (const [key, value] of Object.entries(extracted)) {
        if (value && typeof value === 'string' && value.trim() !== '') {
          const label = fieldLabels[key] || key
          if (!(interview as any)[key] || (interview as any)[key] === 'Infermiere') {
            (updates as any)[key] = value;
            compilati.push(`${label}: ${value.length > 30 ? value.substring(0, 30) + '...' : value}`)
          } else {
            ignorati.push(`${label} (già presente)`)
          }
        }
      }

      if (Object.keys(updates).length > 0) {
        const { error: err } = await supabase
          .from('interviews')
          .update(updates)
          .eq('id', interview.id)
        if (err) throw err
        let msg = `✅ AI OCR completato! ${compilati.length} campi compilati:\n• ${compilati.join('\n• ')}`
        if (ignorati.length > 0) msg += `\n\n⏭️ ${ignorati.length} campi ignorati (già presenti):\n• ${ignorati.join('\n• ')}`
        setSuccess(msg)
        fetchInterviews()
        setSelectedInterview(prev => prev ? { ...prev, ...updates } : null)
      } else {
        const msg = ignorati.length > 0
          ? `AI completata. Nessun nuovo dato aggiunto.\n⏭️ ${ignorati.length} campi ignorati (già presenti):\n• ${ignorati.join('\n• ')}`
          : 'AI completata. Nessun dato utile trovato nei documenti caricati.'
        setSuccess(msg)
      }

      // Store first file as CV link
      if (!interview.link_c_v && files.length > 0) {
        const firstFile = files[0];
        const filePath = `cvs/${interview.id}/${firstFile.name}`
        await supabase.storage.from('documents').upload(filePath, firstFile).catch(() => {})
        const { data: urlData } = supabase.storage.from('documents').getPublicUrl(filePath)
        if (urlData?.publicUrl) {
          await supabase.from('interviews').update({ link_c_v: urlData.publicUrl }).eq('id', interview.id)
        }
      }
    } catch (err: any) {
      setError('Errore OCR: ' + (err.message || 'sconosciuto'))
    } finally {
      setOcrProcessing(false)
      setOcrProgress('')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Caricamento colloqui...
      </div>
    )
  }

  if (fetchError) {
    return (
      <div className="max-w-xl mx-auto mt-8 p-6 bg-red-50 border border-red-200 rounded-2xl text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-red-700 mb-2">Errore Colloqui</h3>
        <p className="text-sm text-red-600 mb-4">{fetchError}</p>
        <button onClick={fetchInterviews} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700">Riprova</button>
      </div>
    )
  }

  // Calendar helpers
  function renderCalendar() {
    const year = calendarMonth.getFullYear()
    const month = calendarMonth.getMonth()
    const firstDay = new Date(year, month, 1).getDay() || 7
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const weeks: (number | null)[][] = []
    let week: (number | null)[] = Array(firstDay - 1).fill(null)
    
    for (let d = 1; d <= daysInMonth; d++) {
      week.push(d)
      if (week.length === 7) { weeks.push(week); week = [] }
    }
    if (week.length > 0) { while (week.length < 7) week.push(null); weeks.push(week) }

    const monthNames = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre']
    
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setCalendarMonth(new Date(year, month - 1))} className="p-2 hover:bg-slate-100 rounded-xl">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-black text-slate-800">{monthNames[month]} {year}</h3>
          <button onClick={() => setCalendarMonth(new Date(year, month + 1))} className="p-2 hover:bg-slate-100 rounded-xl">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {['Lun','Mar','Mer','Gio','Ven','Sab','Dom'].map(d => (
            <div key={d} className="text-xs font-bold text-slate-400 py-2">{d}</div>
          ))}
          {weeks.flat().map((day, i) => {
            const dateStr = day ? `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` : ''
            const dayInterviews = day ? calendarInterviews.filter(iv => iv.data_colloquio === dateStr) : []
            const isToday = day && new Date().toISOString().slice(0,10) === dateStr

            return (
              <div key={i} className={`p-1 min-h-[60px] rounded-lg border text-left ${
                day ? 'border-slate-100 hover:bg-slate-50' : 'border-transparent'
              } ${isToday ? 'bg-indigo-50 border-indigo-200' : ''}`}>
                {day && <div className={`text-xs font-bold mb-1 ${isToday ? 'text-indigo-600' : 'text-slate-500'}`}>{day}</div>}
                {dayInterviews.map(iv => (
                  <div
                    key={iv.id}
                    onClick={() => setSelectedInterview(iv)}
                    className="text-[10px] bg-emerald-100 text-emerald-700 rounded px-1 py-0.5 mb-0.5 truncate cursor-pointer hover:bg-emerald-200"
                  >
                    {iv.cognome} {iv.nome}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // Premium Detail panel
  function renderDetailPanel() {
    if (!selectedInterview) return null
    const iv = selectedInterview
    const noteHist = (iv.note_history as any[]) || []
    const stepHist = (iv.step_history as any[]) || []
    const statoObj = STATI.find(s => s.key === iv.stato)
    const statoIdx = STATI.findIndex(s => s.key === iv.stato)
    const initials = `${(iv.nome || '?')[0]}${(iv.cognome || '?')[0]}`.toUpperCase()
    const pipelineProgress = statoIdx >= 0 ? Math.round(((statoIdx + 1) / STATI.length) * 100) : 0

    // Follow-up countdown
    let followUpDays = 0
    if (iv.data_follow_up) {
      const diff = new Date(iv.data_follow_up).getTime() - Date.now()
      followUpDays = Math.ceil(diff / (1000 * 60 * 60 * 24))
    }

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setSelectedInterview(null)}>
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden border border-white/20 animate-modal-in" onClick={e => e.stopPropagation()}>
          {/* Clean Header — Patient-style */}
          <div className="p-6 pb-0 space-y-5">
            {/* Top Bar */}
            <div className="flex items-center gap-4">
              <button onClick={() => setSelectedInterview(null)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
              <div className="flex-1">
                <h2 className="text-2xl font-black text-slate-800">{iv.cognome} {iv.nome}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-xs font-bold px-2.5 py-0.5 rounded-lg bg-teal-100 text-teal-700">{iv.qualifica || 'Non specificata'}</span>
                  <span className={`text-xs font-bold px-2.5 py-0.5 rounded-lg ${
                    statoObj?.key === 'archiviato' ? 'bg-green-100 text-green-700' :
                    statoObj?.key === 'scartato' ? 'bg-red-100 text-red-700' :
                    'bg-indigo-100 text-indigo-700'
                  }`}>{statoObj?.emoji} {statoObj?.label || iv.stato}</span>
                  {iv.tipo_contratto && <span className={`text-xs font-bold px-2.5 py-0.5 rounded-lg ${iv.tipo_contratto === 'piva' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{iv.tipo_contratto === 'piva' ? 'P.IVA' : 'Ritenuta'}</span>}
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <button onClick={() => { setSelectedInterview(null); handleOpenForm(iv) }} className="p-2 hover:bg-slate-100 rounded-xl text-slate-500 transition-colors" title="Modifica dati">
                    <Edit3 className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            {/* Pipeline Progress — minimal bar like patient progress */}
            <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-2xl p-4 flex items-center gap-4 text-white">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] text-white/70 uppercase font-bold tracking-wider">Pipeline Candidatura</span>
                  <span className="text-xs font-bold">{pipelineProgress}%</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white/80 rounded-full transition-all duration-700" style={{ width: `${pipelineProgress}%` }} />
                </div>
                <div className="flex justify-between mt-1.5">
                  {STATI.map((s, i) => (
                    <div key={s.key} className={`text-[9px] font-bold ${i <= statoIdx ? 'text-white' : 'text-white/30'}`} title={s.label}>
                      {s.emoji}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions — organized by category */}
            <div className="space-y-2">
              {/* Row 1: Contatti */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[9px] font-black text-slate-400 uppercase w-16 shrink-0">Contatti</span>
                {iv.telefono && <a href={`tel:${iv.telefono}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl text-xs font-bold transition-all border border-blue-200"><Phone className="w-3.5 h-3.5" /> Chiama</a>}
                {iv.email && <button onClick={(e) => { e.preventDefault(); sendAptosFormattedEmail(iv.email || '', '', ''); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700 rounded-xl text-xs font-bold transition-all border border-violet-200"><Mail className="w-3.5 h-3.5" /> Email</button>}
                {isAdmin && (iv.stato === 'documenti_ricevuti' || iv.stato === 'idoneo' || iv.stato === 'invio_contratto') && <button onClick={() => handleSendContractToCarlo(iv)} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold transition-all border border-indigo-200"><Mail className="w-3.5 h-3.5" /> Invia a Carlo</button>}
              </div>
              {/* Row 2: Documenti & Contratti */}
              {isAdmin && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[9px] font-black text-slate-400 uppercase w-16 shrink-0">Contratti</span>
                  {iv.tipo_contratto === 'piva' && iv.email && <button onClick={() => handleSendDocRequestPIVA(iv)} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-xl text-xs font-bold transition-all border border-orange-200"><Mail className="w-3.5 h-3.5" /> Richiedi Doc P.IVA</button>}
                  {iv.tipo_contratto === 'ritenuta' && iv.email && <button onClick={() => handleSendDocRequestRitenuta(iv)} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-xl text-xs font-bold transition-all border border-amber-200"><Mail className="w-3.5 h-3.5" /> Richiedi Doc Ritenuta</button>}
                  {!iv.tipo_contratto && iv.email && <button onClick={() => handleSendDocRequestPIVA(iv)} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-xl text-xs font-bold transition-all border border-orange-200"><Mail className="w-3.5 h-3.5" /> Richiedi Doc P.IVA</button>}
                  {!iv.tipo_contratto && iv.email && <button onClick={() => handleSendDocRequestRitenuta(iv)} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-xl text-xs font-bold transition-all border border-amber-200"><Mail className="w-3.5 h-3.5" /> Richiedi Doc Ritenuta</button>}
                  
                  {iv.stato === 'richiesta_documenti' && iv.email && (() => {
                    const historyMs = Math.max(0, ...((iv.step_history as any[]) || []).filter(h => h.stepId === 'richiesta_documenti').map(h => new Date(h.timestamp).getTime()));
                    const days = historyMs > 0 ? Math.floor((new Date().getTime() - historyMs) / (1000 * 60 * 60 * 24)) : 0;
                    const isAlert = days >= 5;
                    return (
                      <button onClick={() => handleSollecitoDocumenti(iv)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${isAlert ? 'bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200 shadow-[0_0_8px_rgba(225,29,72,0.4)] animate-pulse' : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'}`}>
                        <Mail className="w-3.5 h-3.5" /> 
                        {isAlert ? `⚠️ Sollecito Urgente (${days}gg)` : 'Sollecito Doc'}
                      </button>
                    )
                  })()}
                  
                  {(iv.stato === 'idoneo' || iv.stato === 'invio_contratto' || iv.stato === 'documenti_ricevuti') && (iv.tipo_contratto === 'piva' || !iv.tipo_contratto) && <button onClick={() => handleGenerateContract(iv, 'piva')} className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 rounded-xl text-xs font-bold transition-all border border-cyan-200"><Briefcase className="w-3.5 h-3.5" /> Genera Contratto P.IVA</button>}
                  {(iv.stato === 'idoneo' || iv.stato === 'invio_contratto' || iv.stato === 'documenti_ricevuti') && (iv.tipo_contratto === 'ritenuta' || !iv.tipo_contratto) && <button onClick={() => handleGenerateContract(iv, 'ritenuta')} className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-xl text-xs font-bold transition-all border border-teal-200"><Briefcase className="w-3.5 h-3.5" /> Genera Contratto Ritenuta</button>}
                </div>
              )}
              {/* Row 3: Azioni */}
              {isAdmin && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[9px] font-black text-slate-400 uppercase w-16 shrink-0">Azioni</span>
                  <button onClick={() => handleVerifyOPI(iv)} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl text-xs font-bold transition-all border border-emerald-200"><Shield className="w-3.5 h-3.5" /> Verifica OPI</button>
                  {(iv.stato === 'idoneo' || iv.stato === 'invio_contratto' || iv.stato === 'documenti_ricevuti' || iv.stato === 'contratto_firmato') && <button onClick={() => handleArchiveTransfer(iv)} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-xl text-xs font-bold transition-all border border-green-200"><FileText className="w-3.5 h-3.5" /> Archivia in Dashboard</button>}
                  <label className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-xl text-xs font-bold transition-all cursor-pointer border border-purple-200">
                    {ocrProcessing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} A.I. Auto-Aggiorna
                    <input type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.txt" multiple disabled={ocrProcessing} onChange={e => { const fs = e.target.files; if (fs && fs.length > 0) handleCVUpload(iv, fs); e.target.value = '' }} />
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 space-y-5 flex-1 overflow-y-auto">
            {/* OCR progress */}
            {ocrProcessing && (
              <div className="flex items-center gap-3 p-3 bg-violet-50 border border-violet-200 rounded-xl animate-pulse">
                <Loader2 className="w-4 h-4 text-violet-600 animate-spin" />
                <span className="text-sm font-medium text-violet-700">{ocrProgress || 'Elaborazione CV...'}</span>
              </div>
            )}

            {/* Follow-up countdown */}
            {iv.data_follow_up && (
              <div className={`flex items-center gap-3 p-3 rounded-xl border ${followUpDays <= 0 ? 'bg-red-50 border-red-200' : followUpDays <= 3 ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                <Calendar className="w-4 h-4 text-current" />
                <span className="text-sm font-medium">
                  Follow-up: {new Date(iv.data_follow_up).toLocaleDateString('it-IT')}
                  {followUpDays > 0 ? ` (tra ${followUpDays} giorni)` : followUpDays === 0 ? ' (OGGI!)' : ` (${Math.abs(followUpDays)} giorni fa)`}
                </span>
              </div>
            )}

            {/* Contact & Info — Semantic cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Contatti */}
              <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Contatti</div>
                {iv.telefono && <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-blue-500" /> <a href={`tel:${iv.telefono}`} className="text-blue-600 hover:underline">{iv.telefono}</a></div>}
                {iv.email && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-indigo-500" /> <button onClick={(e) => { e.preventDefault(); sendAptosFormattedEmail(iv.email || '', '', ''); }} className="text-indigo-600 hover:underline truncate text-left">{iv.email}</button></div>}
                {iv.pec_contratto && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-violet-500" /> <span className="text-slate-600 truncate" title={iv.pec_contratto}>PEC: {iv.pec_contratto}</span></div>}
                {iv.codice_fiscale && <div className="flex items-center gap-2 text-sm mt-2 pt-2 border-t border-slate-100"><FileText className="w-4 h-4 text-amber-500" /> <span className="font-mono text-slate-700">{iv.codice_fiscale}</span></div>}
                {iv.partita_iva && <div className="flex items-center gap-2 text-sm"><FileText className="w-4 h-4 text-orange-500" /> <span className="text-slate-700">P.IVA: <span className="font-mono font-bold">{iv.partita_iva}</span></span></div>}
                {iv.tipo_contratto && <div className="flex items-center gap-2 text-sm"><Briefcase className="w-4 h-4 text-indigo-500" /> <span className="text-slate-700">{iv.tipo_contratto === 'piva' ? 'P.IVA' : 'Ritenuta d\'Acconto'}</span></div>}
              </div>

              {/* Coordinate */}
              <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Coordinate</div>
                {iv.regione && <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-emerald-500" /> {iv.regione}{iv.provincia ? ` — ${iv.provincia}` : ''}</div>}
                {iv.zone_coperte && <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-teal-500" /> Zone: {iv.zone_coperte}</div>}
                {iv.km_spostamento && <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-sky-500" /> Max {iv.km_spostamento} km</div>}
                {iv.residenza_contratto && <div className="flex items-center gap-2 text-sm mt-2 pt-2 border-t border-slate-100"><MapPin className="w-4 h-4 text-slate-400" /> <span className="text-slate-600 text-xs">Residenza: {iv.residenza_contratto}</span></div>}
              </div>
            </div>

            {/* Billing KPI banner */}
            {nurseBilling && nurseBilling.totaleAccessi > 0 && (
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-4 flex items-center gap-4 text-white">
                <div className="flex-1">
                  <div className="text-xs font-bold text-white/70 uppercase tracking-wider">Fatturato Globale</div>
                  <div className="text-lg font-black mt-0.5">€{nurseBilling.totaleLordo.toFixed(2)}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold">{nurseBilling.totaleAccessi} {nurseBilling.totaleAccessi === 1 ? 'accesso' : 'accessi'}</div>
                  <div className="text-xs text-white/70">{nurseBilling.totaleOre}h lavorate</div>
                </div>
              </div>
            )}

            {/* Dati Professionali — always show */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
              <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Dati Professionali</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {iv.albo_iscrizione && (
                  <div className="space-y-1">
                    <div className="text-[10px] text-slate-400 uppercase font-bold">Albo & N°</div>
                    <div className="text-sm font-medium text-slate-700">{iv.albo_iscrizione} — <span className="text-indigo-600 font-bold">{iv.albo_numero || 'N/D'}</span></div>
                    {iv.data_iscrizione_albo && (
                      <div className="text-[10px] text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3"/> Iscrizione: {new Date(iv.data_iscrizione_albo).toLocaleDateString('it-IT')}</div>
                    )}
                    {iv.data_abilitazione && (
                      <div className="text-[10px] text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3"/> Abilitazione: {new Date(iv.data_abilitazione).toLocaleDateString('it-IT')}</div>
                    )}
                  </div>
                )}
                {iv.polizza_compagnia && (
                  <div className="space-y-1">
                    <div className="text-[10px] text-slate-400 uppercase font-bold">Assicurazione</div>
                    <div className="text-sm font-medium text-slate-700">{iv.polizza_compagnia} <span className="text-slate-500 text-xs">#{iv.polizza_numero}</span></div>
                    {(iv.polizza_massimale || iv.polizza_scadenza) && (
                      <div className="text-[10px] text-slate-500 flex justify-between items-center">
                        {iv.polizza_massimale && <span>Max: €{iv.polizza_massimale}</span>}
                        {iv.polizza_scadenza && <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/> Scad: {new Date(iv.polizza_scadenza).toLocaleDateString('it-IT')}</span>}
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-1">
                  <div className="text-[10px] text-slate-400 uppercase font-bold">IBAN</div>
                  <div className="font-mono text-xs font-bold text-slate-700 break-all">{iv.iban || <span className="text-slate-300 italic">Non inserito</span>}</div>
                </div>
                {iv.link_c_v && (
                  <a href={iv.link_c_v} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-sm font-bold transition-all border border-indigo-200">
                    <ExternalLink className="w-4 h-4" /> Visualizza CV
                  </a>
                )}
              </div>
            </div>

            {/* Rating */}
            {iv.punteggio && iv.punteggio > 0 && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <span className="text-xs font-bold text-amber-700 uppercase">Punteggio</span>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-4 h-4 ${s <= (iv.punteggio || 0) ? 'text-amber-500 fill-amber-500' : 'text-amber-200'}`} />)}
                </div>
                <span className="text-sm font-black text-amber-700 ml-auto">{iv.punteggio}/5</span>
              </div>
            )}

            {/* Notes */}
            {iv.note && <div><h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">📝 Note</h4><p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3 border border-slate-100 whitespace-pre-line">{iv.note}</p></div>}

            {/* Unified Timeline */}
            {(noteHist.length > 0 || stepHist.length > 0) && (
              <div className="bg-gradient-to-r from-slate-50 to-white rounded-2xl p-4 border border-slate-200">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Timeline ({noteHist.length + stepHist.length})</h4>
                <div className="relative pl-4 border-l-2 border-emerald-200 space-y-2.5 max-h-60 overflow-y-auto">
                  {[...noteHist.map((note: any) => ({ ...note, _type: 'note', _date: new Date(note.date) })), ...stepHist.map((s: any) => ({ ...s, _type: 'step', _date: new Date(s.timestamp) }))].sort((a, b) => b._date.getTime() - a._date.getTime()).map((item: any, idx: number) => (
                    <div key={idx} className="relative">
                      <div className={`absolute -left-[21px] w-2.5 h-2.5 rounded-full border-2 border-white ${item._type === 'note' ? 'bg-indigo-400' : 'bg-emerald-400'}`} />
                      <div className="bg-white rounded-lg p-2.5 border border-slate-100 shadow-sm">
                        <p className="text-xs text-slate-700">{item._type === 'note' ? item.text : <><span className="font-bold text-emerald-600">{item.stepLabel}</span></>}</p>
                        <div className="text-[10px] text-slate-400 mt-1">{item._date.toLocaleString('it-IT')}{item.stepLabel && item._type === 'note' && <span className="ml-1 px-1 py-0.5 bg-slate-100 rounded text-[10px] font-bold">{item.stepLabel}</span>}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Note */}
            {isAdmin && (
              <div className="flex gap-2">
                <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Aggiungi nota..." className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" onKeyDown={e => { if (e.key === 'Enter') handleAddNote(iv) }} />
                <button onClick={() => handleAddNote(iv)} disabled={!newNote.trim()} className="px-4 py-2 bg-indigo-500 text-white rounded-xl text-sm font-bold hover:bg-indigo-600 disabled:opacity-50 transition-colors">Aggiungi</button>
              </div>
            )}

            {/* Move Status */}
            {isAdmin && (
              <div className="bg-gradient-to-r from-slate-50 to-white rounded-2xl p-4 border border-slate-200">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">🔄 Sposta a</h4>
                <div className="flex flex-wrap gap-2">
                  {STATI.filter(s => s.key !== iv.stato).map(s => (
                    <button key={s.key} onClick={() => { handleMoveStatus(iv, s.key); setSelectedInterview(null) }}
                      className="text-xs px-3 py-1.5 rounded-xl bg-white hover:bg-indigo-50 text-slate-600 hover:text-indigo-600 border border-slate-200 hover:border-indigo-200 transition-all flex items-center gap-1.5 shadow-sm hover:shadow">
                      <span>{s.emoji}</span> {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {iv.created_at && <div className="text-[10px] text-slate-300 text-center uppercase tracking-wider">Colloquio creato il {new Date(iv.created_at).toLocaleDateString('it-IT')} • ID: {iv.id.substring(0, 8)}</div>}
          </div>
        </div>
      </div>
    )
  }

  // === OUTLOOK ICS IMPORT ===
  async function handleImportICS(file: File) {
    try {
      const text = await file.text()
      const events: { summary: string; date: string; description: string; attendees: string[] }[] = []

      // Parse ICS VEVENT blocks
      const vevents = text.split('BEGIN:VEVENT')
      for (let i = 1; i < vevents.length; i++) {
        const block = vevents[i].split('END:VEVENT')[0]

        const getSummary = () => {
          const m = block.match(/SUMMARY[^:]*:(.+)/)
          return m ? m[1].trim() : ''
        }
        const getDate = () => {
          const m = block.match(/DTSTART[^:]*:(\d{4})(\d{2})(\d{2})/)
          return m ? `${m[1]}-${m[2]}-${m[3]}` : ''
        }
        const getDescription = () => {
          const m = block.match(/DESCRIPTION[^:]*:(.+?)(?=\r?\n[A-Z])/s)
          return m ? m[1].replace(/\\n/g, '\n').replace(/\\,/g, ',').trim() : ''
        }
        const getAttendees = () => {
          const matches = [...block.matchAll(/ATTENDEE[^:]*:mailto:([^\r\n]+)/gi)]
          return matches.map(m => m[1].trim())
        }

        const summary = getSummary()
        const date = getDate()
        if (summary && date) {
          events.push({ summary, date, description: getDescription(), attendees: getAttendees() })
        }
      }

      if (events.length === 0) {
        setError('Nessun evento trovato nel file ICS.')
        return
      }

      // Check for duplicates by date + similar name
      let imported = 0
      let skipped = 0
      for (const ev of events) {
        // Check if already exists
        const existing = interviews.find(iv =>
          iv.data_colloquio === ev.date &&
          ev.summary.toLowerCase().includes((iv.cognome || '').toLowerCase())
        )
        if (existing) {
          skipped++
          continue
        }

        // Try to extract name from summary ("Colloquio Mario Rossi" or just "Mario Rossi")
        const nameClean = ev.summary
          .replace(/colloquio/gi, '')
          .replace(/^[\s-:]+/, '')
          .trim()
        const nameParts = nameClean.split(/\s+/)
        const nome = nameParts[0] || ''
        const cognome = nameParts.slice(1).join(' ') || ''

        // Try to extract email from attendees
        const email = ev.attendees.find(a => !a.includes('quicare')) || ev.attendees[0] || ''

        const newInterview: Partial<Interview> = {
          nome,
          cognome,
          email,
          data_colloquio: ev.date,
          stato: 'colloquio_fissato',
          note: ev.description || `Importato da Outlook: ${ev.summary}`,
          qualifica: 'Infermiere',
        }

        const { error: err } = await supabase.from('interviews').insert(newInterview)
        if (!err) imported++
      }

      setSuccess(`\u2705 Import ICS: ${imported} colloqui importati, ${skipped} gi\u00e0 presenti (${events.length} eventi totali)`)
      fetchInterviews()
    } catch (err: any) {
      setError('Errore importazione ICS: ' + (err.message || 'formato non valido'))
    }
  }

  // --- A.I. Executive Summary Generator (Interviews) ---
  async function generateInterviewSummary(interviewsList: Interview[], type: string): Promise<string> {
    let apiKey = localStorage.getItem('GEMINI_API_KEY');
    if (!apiKey) return "Sommario A.I. non disponibile (API Key mancante).";
    
    const leanData = interviewsList.map(i => ({
      regione: i.regione,
      stato: i.stato,
      data: i.data_colloquio || 'N/A'
    }));
    
    try {
      const promptText = `Sei l'AI direzionale HR. Analizza questo array di colloqui infermieristici. 
Scrivi un "Executive Summary" (circa 120 parole) formale descrivendo i trend attuali: regioni di maggiore interesse, proporzione degli esiti (positivi, in attesa, non idonei) ed eventuali colli di bottiglia e consigli operativi. Testo discorsivo, niente markdown o elenchi puntati.
Dati: ${JSON.stringify(leanData)}`;
      
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: promptText }] }] })
      });
      if (!response.ok) return "Errore A.I.: Impossibile generare il sommario.";
      const data = await response.json();
      let aiText = data.candidates?.[0]?.content?.parts?.[0]?.text || "Nessun output.";
      return aiText.replace(/\*/g, '').trim();
    } catch {
      return "Errore A.I. interno.";
    }
  }

  // === INTERVIEW REPORT EXPORTS ===
  async function handleExportInterviewExcel() {
    setSuccess('Analisi A.I. per Excel in corso...');
    const today = new Date().toLocaleDateString('it-IT')
    
    const sorted = [...interviews].sort((a, b) => {
      const statiOrder = STATI.map(s => s.key)
      return statiOrder.indexOf(a.stato) - statiOrder.indexOf(b.stato)
    })

    const wb = new ExcelJS.Workbook()
    const wsMain = wb.addWorksheet('Report Colloqui')

    const columnsMain = [
      { header: '#', key: 'id', width: 5 }, { header: 'Cognome', key: 'cognome', width: 18 }, { header: 'Nome', key: 'nome', width: 16 },
      { header: 'Qualifica', key: 'qualifica', width: 16 }, { header: 'Stato', key: 'stato', width: 22 }, { header: 'Regione', key: 'regione', width: 18 },
      { header: 'Provincia', key: 'provincia', width: 18 }, { header: 'Telefono', key: 'telefono', width: 16 }, { header: 'Email', key: 'email', width: 28 },
      { header: 'Tipo Contratto', key: 'contratto', width: 18 }, { header: 'Km', key: 'km', width: 10 }, { header: 'Zone Coperte', key: 'zone', width: 24 },
      { header: 'Data Colloquio', key: 'data', width: 16 }, { header: 'Punt.', key: 'punteggio', width: 8 }, { header: 'Note', key: 'note', width: 35 }
    ]
    wsMain.columns = columnsMain

    const positivi = interviews.filter(i => ['esito_positivo','documenti_ricevuti','invio_contratto','richiesta_documenti'].includes(i.stato)).length
    const negativi = interviews.filter(i => ['non_idoneo'].includes(i.stato)).length
    const inCorso = interviews.length - positivi - negativi

    wsMain.spliceRows(1, 0, [], [], [])
    wsMain.mergeCells('A1:O1')
    wsMain.getRow(1).getCell(1).value = `REPORT COLLOQUI — QuiCare Srl`
    wsMain.getRow(1).font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } }
    wsMain.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF312E81' } }
    wsMain.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' }
    wsMain.getRow(1).height = 30

    wsMain.mergeCells('A2:O2')
    wsMain.getRow(2).getCell(1).value = `${interviews.length} candidati  |  ${positivi} positivi  |  ${inCorso} in corso  |  ${negativi} esclusi  |  Aggiornato al ${today}`
    wsMain.getRow(2).font = { size: 11, bold: true, color: { argb: 'FFFFFFFF' } }
    wsMain.getRow(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4338CA' } }
    wsMain.getRow(2).alignment = { vertical: 'middle', horizontal: 'center' }
    wsMain.getRow(2).height = 25

    // Spacer
    wsMain.getRow(3).height = 10

    // Row 4: A.I. Executive Summary Title
    wsMain.mergeCells('B4:H4')
    wsMain.getRow(4).height = 24
    const aiTitle = wsMain.getRow(4).getCell(2)
    aiTitle.value = '✨ EXECUTIVE SUMMARY A.I.'
    aiTitle.font = { size: 12, bold: true, color: { argb: 'FF4F46E5' } }
    aiTitle.alignment = { vertical: 'middle' }

    // Row 5-7: A.I. Executive Summary Text
    wsMain.mergeCells('B5:L8')
    const aiBody = wsMain.getRow(5).getCell(2)
    aiBody.value = await generateInterviewSummary(sorted, 'Excel')
    aiBody.font = { size: 10, italic: true, color: { argb: 'FF475569' } }
    aiBody.alignment = { vertical: 'top', horizontal: 'left', wrapText: true }
    aiBody.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } }
    aiBody.border = { top: { style: 'thin', color: { argb: 'FFE2E8F0' } }, bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } }, left: { style: 'thin', color: { argb: 'FFE2E8F0' } }, right: { style: 'thin', color: { argb: 'FFE2E8F0' } } }

    // Spacer
    wsMain.getRow(9).height = 15

    const hr = wsMain.getRow(10)
    hr.values = columnsMain.map(c => c.header)
    hr.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    hr.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } }
    hr.alignment = { vertical: 'middle', horizontal: 'center' }
    hr.height = 25

    let cr = 11
    sorted.forEach((iv, i) => {
      const statoObj = STATI.find(s => s.key === iv.stato)
      const row = wsMain.getRow(cr)
      row.values = [
        i + 1, iv.cognome, iv.nome, iv.qualifica || '', statoObj?.label || iv.stato,
        iv.regione || '', iv.provincia || '', iv.telefono || '', iv.email || '',
        iv.tipo_contratto || '', iv.km_spostamento || '', iv.zone_coperte || '',
        iv.data_colloquio ? new Date(iv.data_colloquio).toLocaleDateString('it-IT') : '',
        iv.punteggio ?? '', iv.note || ''
      ]
      
      row.alignment = { vertical: 'top', wrapText: true }
      if (i % 2 === 0) row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } }
      
      row.eachCell((cell) => {
        cell.border = {
          top: {style:'thin', color: {argb:'FFE2E8F0'}}, left: {style:'thin', color: {argb:'FFE2E8F0'}},
          bottom: {style:'thin', color: {argb:'FFE2E8F0'}}, right: {style:'thin', color: {argb:'FFE2E8F0'}}
        }
      })
      cr++
    })
    
    wsMain.views = [{ state: 'frozen', xSplit: 0, ySplit: 4 }]

    const buf = await wb.xlsx.writeBuffer()
    saveAs(new Blob([buf]), `QuiCare_Report_Colloqui_${new Date().toISOString().slice(0, 10)}.xlsx`)
    setSuccess('✅ Report colloqui Excel esportato!')
  }

  async function handleExportInterviewPDF() {
    setSuccess('Analisi A.I. per Export PDF in corso...');
    const doc: any = new jsPDF({ orientation: 'landscape' })
    const today = new Date().toLocaleDateString('it-IT')
    const pageW = doc.internal.pageSize.width
    const pageH = doc.internal.pageSize.height

    const positivi = interviews.filter(i => ['andato_bene','idoneo','contratto_firmato','documenti_ricevuti','invio_contratto'].includes(i.stato)).length
    const negativi = interviews.filter(i => ['non_idoneo','escluso'].includes(i.stato)).length
    const inCorso = interviews.length - positivi - negativi

    // Header
    doc.setFillColor(49, 46, 129)
    doc.rect(0, 0, pageW, 32, 'F')
    doc.setFillColor(16, 185, 129)
    doc.rect(0, 0, pageW * 0.6, 32, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text('REPORT COLLOQUI', 14, 14)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.text(`${interviews.length} candidati totali`, 14, 22)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text('QuiCare Srl', pageW - 14, 12, { align: 'right' })
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.text(`Report generato il ${today}`, pageW - 14, 20, { align: 'right' })

    // KPI bar
    const kpiY = 36
    const kpiBoxW = (pageW - 28 - 9) / 4
    const kpis = [
      { label: 'Totale', value: `${interviews.length}`, color: [79, 70, 229] },
      { label: 'Positivi', value: `${positivi}`, color: [16, 185, 129] },
      { label: 'In Corso', value: `${inCorso}`, color: [245, 158, 11] },
      { label: 'Esclusi', value: `${negativi}`, color: [239, 68, 68] },
    ]
    kpis.forEach((kpi, i) => {
      const x = 14 + i * (kpiBoxW + 3)
      doc.setFillColor(248, 250, 252)
      doc.roundedRect(x, kpiY, kpiBoxW, 14, 2, 2, 'F')
      doc.setFillColor(kpi.color[0], kpi.color[1], kpi.color[2])
      doc.roundedRect(x, kpiY, 2.5, 14, 1, 1, 'F')
      doc.setTextColor(kpi.color[0], kpi.color[1], kpi.color[2])
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text(kpi.value, x + 7, kpiY + 7)
      doc.setTextColor(100, 116, 139)
      doc.setFontSize(6.5)
      doc.text(kpi.label, x + 7, kpiY + 12)
    })

    // === AI EXECUTIVE SUMMARY ===
    const sortedForAI = [...interviews].sort((a, b) => STATI.findIndex(s => s.key === a.stato) - STATI.findIndex(s => s.key === b.stato));
    const aiSummaryText = await generateInterviewSummary(sortedForAI, 'PDF');
    const aiY = kpiY + 18;
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.roundedRect(14, aiY, pageW - 28, 22, 2, 2, 'FD');
    
    doc.setTextColor(79, 70, 229);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('✨ EXECUTIVE SUMMARY A.I.', 18, aiY + 6);
    
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(aiSummaryText, 18, aiY + 11, { maxWidth: pageW - 36, lineHeightFactor: 1.4 });

    // Table
    const sorted = [...interviews].sort((a, b) => {
      const order = STATI.map(s => s.key)
      return order.indexOf(a.stato) - order.indexOf(b.stato)
    })
    const tableData = sorted.map((iv, i) => {
      const statoObj = STATI.find(s => s.key === iv.stato)
      return [
        i + 1, `${iv.cognome} ${iv.nome}`, iv.qualifica || '',
        statoObj?.label || iv.stato,
        iv.regione ? `${iv.regione}\n${iv.provincia || ''}` : '',
        iv.telefono || '', iv.email || '',
        iv.data_colloquio ? new Date(iv.data_colloquio).toLocaleDateString('it-IT') : '—',
      ]
    })

    autoTable(doc, {
      startY: aiY + 26,
      head: [['#', 'Nome', 'Qualifica', 'Stato', 'Regione/Prov.', 'Telefono', 'Email', 'Data Coll.']],
      body: tableData,
      styles: { fontSize: 7.5, cellPadding: { top: 2, right: 3, bottom: 2, left: 3 }, lineColor: [226, 232, 240], lineWidth: 0.2 },
      headStyles: { fillColor: [16, 185, 129], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' },
        1: { cellWidth: 42, fontStyle: 'bold' },
        2: { cellWidth: 24 },
        3: { cellWidth: 30 },
        4: { cellWidth: 34 },
        5: { cellWidth: 28 },
        6: { cellWidth: 48 },
        7: { cellWidth: 20, halign: 'center' },
      },
      margin: { left: 10, right: 10 },
      didParseCell: (data: any) => {
        if (data.section === 'body' && data.column.index === 3) {
          const text = data.cell.text[0] || ''
          if (['Andato Bene','Idoneo','Contratto Firmato'].includes(text)) data.cell.styles.textColor = [16, 185, 129]
          else if (['Non Idoneo','Escluso'].includes(text)) data.cell.styles.textColor = [239, 68, 68]
          else data.cell.styles.textColor = [245, 158, 11]
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    // Footer
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setDrawColor(226, 232, 240)
      doc.setLineWidth(0.3)
      doc.line(14, pageH - 14, pageW - 14, pageH - 14)
      doc.setFontSize(7)
      doc.setTextColor(148, 163, 184)
      doc.text(`Pagina ${i} di ${pageCount}`, 14, pageH - 8)
      doc.setFont('helvetica', 'bold')
      doc.text('GESTIONE INFERMIERI © QuiCare Srl', pageW / 2, pageH - 8, { align: 'center' })
      doc.setFont('helvetica', 'normal')
      doc.text('Documento riservato', pageW - 14, pageH - 8, { align: 'right' })
    }

    doc.save(`QuiCare_Report_Colloqui_${new Date().toISOString().slice(0, 10)}.pdf`)
    setSuccess('✅ Report colloqui PDF esportato!')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg shadow-emerald-500/20">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-800">Gestione Colloqui</h2>
              <p className="text-sm text-slate-400">{interviews.length} candidati</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex bg-slate-100 rounded-xl p-1">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'kanban' ? 'bg-white shadow text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Kanban
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${viewMode === 'calendar' ? 'bg-white shadow text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <Calendar className="w-3.5 h-3.5" /> Calendario
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'list' ? 'bg-white shadow text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Lista
              </button>
            </div>
            {isAdmin && (
              <button
                onClick={() => handleOpenForm()}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all text-sm"
              >
                <Plus className="w-4 h-4" /> Nuovo Colloquio
              </button>
            )}
            {/* Export buttons */}
            <button onClick={handleExportInterviewExcel} className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
            </button>
            <button onClick={handleExportInterviewPDF} className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl hover:bg-rose-100">
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
            {isAdmin && (
              <label className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> Outlook
                <input
                  type="file"
                  className="hidden"
                  accept=".ics,.ical"
                  onChange={e => {
                    const file = e.target.files?.[0]
                    if (file) handleImportICS(file)
                    e.target.value = ''
                  }}
                />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Cerca candidato per nome, cognome, qualifica, regione, telefono..."
          className="w-full pl-11 pr-10 py-3 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white shadow-sm"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        )}
        {searchQuery && (
          <div className="absolute right-12 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-bold">
            {filteredInterviews.length}/{interviews.length}
          </div>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm animate-fade-in">
          <AlertCircle className="w-4 h-4" /> {error}
          <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-2 text-green-700 text-sm animate-fade-in max-h-60 overflow-y-auto">
          <Check className="w-4 h-4 mt-0.5 shrink-0" /> <span className="whitespace-pre-line flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Calendar view */}
      {viewMode === 'calendar' && renderCalendar()}

      {/* List view */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Candidato</th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Qualifica</th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Recapiti</th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Località</th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Stato</th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Data Colloquio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredInterviews.map(iv => {
                const statoObj = STATI.find(s => s.key === iv.stato)
                const qualChip: Record<string, string> = { 'Infermiere': 'bg-blue-100 text-blue-700', 'OSS': 'bg-emerald-100 text-emerald-700', 'Fisioterapista': 'bg-amber-100 text-amber-700', 'Logopedista': 'bg-violet-100 text-violet-700' }
                const initials = `${(iv.cognome || '?')[0]}${(iv.nome || '?')[0]}`.toUpperCase()
                return (
                  <tr
                    key={iv.id}
                    onClick={() => setSelectedInterview(iv)}
                    className="hover:bg-indigo-50/50 cursor-pointer transition-colors group"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center text-white text-xs font-black shadow-sm ${statoObj?.dot === 'bg-slate-400' ? 'from-slate-400 to-slate-500' : statoObj?.dot === 'bg-blue-500' ? 'from-blue-400 to-blue-600' : statoObj?.dot === 'bg-emerald-500' ? 'from-emerald-400 to-emerald-600' : statoObj?.dot === 'bg-green-600' ? 'from-green-500 to-green-700' : statoObj?.dot === 'bg-red-500' ? 'from-red-400 to-red-600' : 'from-indigo-400 to-indigo-600'} bg-gradient-to-br`}>
                          {initials}
                        </div>
                        <div>
                          <div className="font-bold text-slate-800">{iv.cognome} {iv.nome}</div>
                          {iv.punteggio && <div className="flex items-center gap-0.5 text-[10px] text-amber-500 font-bold"><Star className="w-2.5 h-2.5" />{iv.punteggio}/10</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-bold px-2 py-1 rounded-lg ${qualChip[iv.qualifica || ''] || 'bg-slate-100 text-slate-600'}`}>
                        {iv.qualifica || 'N/D'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 text-xs">
                        {iv.telefono && <span className="flex items-center gap-1.5 text-slate-600"><Phone className="w-3.5 h-3.5 text-slate-400"/> {iv.telefono}</span>}
                        {iv.email && <span className="flex items-center gap-1.5 text-slate-600"><Mail className="w-3.5 h-3.5 text-slate-400"/> <span className="truncate max-w-[150px]" title={iv.email}>{iv.email}</span></span>}
                        {!iv.telefono && !iv.email && <span className="text-slate-400">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {iv.regione ? (
                        <div className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-slate-400" />
                          <div>{iv.regione}<br/><span className="text-[10px] text-slate-400">{iv.provincia}</span></div>
                        </div>
                      ) : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold border ${statoObj?.color || ''}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statoObj?.dot || ''}`} />
                        {statoObj?.label || iv.stato}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {iv.data_colloquio ? (
                         <div className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                           <Calendar className="w-3.5 h-3.5 text-indigo-400" />
                           {new Date(iv.data_colloquio).toLocaleDateString('it-IT')}
                         </div>
                      ) : <span className="text-slate-400">-</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Kanban board */}
      {viewMode === 'kanban' && (
        <div>
          {/* Top scrollbar */}
          <div
            ref={topScrollRef}
            onScroll={handleTopScroll}
            className="overflow-x-auto mb-2"
            style={{ height: '12px' }}
          >
            <div style={{ width: `${topScrollWidth}px`, height: '1px' }} />
          </div>
          <div ref={kanbanRef} onScroll={handleKanbanScroll} className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '300px' }}>
          {STATI.map(stato => (
            <div key={stato.key} className="min-w-[200px] flex-shrink-0">
              <div className={`${stato.color} border rounded-xl p-3 mb-3 flex items-center justify-between`}>
                <span className="text-xs font-black uppercase tracking-wider flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${stato.dot}`} />
                  {stato.label}
                </span>
                <span className="text-xs font-bold bg-white/60 px-2 py-0.5 rounded-full">
                  {(grouped[stato.key] || []).length}
                </span>
              </div>

              <div className="space-y-2 stagger-children">
                {(grouped[stato.key] || []).map((interview, idx) => {
                  const initials = `${(interview.cognome || '?')[0]}${(interview.nome || '?')[0]}`.toUpperCase()
                  const stIdx = STATI.findIndex(s => s.key === interview.stato)
                  const progress = stIdx >= 0 ? Math.round(((stIdx + 1) / STATI.length) * 100) : 0
                  const noteCount = ((interview.note_history as any[]) || []).length
                  const qualChip: Record<string, string> = { 'Infermiere': 'bg-blue-100 text-blue-700', 'OSS': 'bg-emerald-100 text-emerald-700', 'Fisioterapista': 'bg-amber-100 text-amber-700', 'Logopedista': 'bg-violet-100 text-violet-700' }
                  return (
                  <div
                    key={interview.id}
                    onClick={() => setSelectedInterview(interview)}
                    className="relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 p-3.5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
                  >
                    {/* Left gradient accent */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${stato.dot} rounded-l-2xl`} />
                    
                    <div className="flex items-start gap-3 pl-2">
                      {/* Avatar */}
                      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${stato.dot === 'bg-slate-400' ? 'from-slate-400 to-slate-500' : stato.dot === 'bg-blue-500' ? 'from-blue-400 to-blue-600' : stato.dot === 'bg-emerald-500' ? 'from-emerald-400 to-emerald-600' : stato.dot === 'bg-green-600' ? 'from-green-500 to-green-700' : stato.dot === 'bg-red-500' ? 'from-red-400 to-red-600' : 'from-indigo-400 to-indigo-600'} flex items-center justify-center text-white text-xs font-black flex-shrink-0 shadow-sm`}>
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="font-bold text-slate-800 text-sm truncate">{interview.cognome} {interview.nome}</div>
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                            {isAdmin && (
                              <>
                                <button onClick={(e) => { e.stopPropagation(); handleOpenForm(interview) }} className="p-1 hover:bg-indigo-50 rounded-md"><Edit3 className="w-3 h-3 text-indigo-500" /></button>
                                <button onClick={(e) => { e.stopPropagation(); handleDelete(interview.id, `${interview.cognome} ${interview.nome}`) }} className="p-1 hover:bg-red-50 rounded-md"><Trash2 className="w-3 h-3 text-red-400" /></button>
                              </>
                            )}
                          </div>
                        </div>
                        {interview.qualifica && <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-md mt-0.5 ${qualChip[interview.qualifica] || 'bg-slate-100 text-slate-600'}`}>{interview.qualifica}</span>}
                        
                        {/* Info row */}
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {interview.telefono && <span className="flex items-center gap-0.5 text-[10px] text-slate-400"><Phone className="w-2.5 h-2.5" />{interview.telefono}</span>}
                          {interview.data_colloquio && <span className="text-[10px] text-slate-400">📅 {new Date(interview.data_colloquio).toLocaleDateString('it-IT')}</span>}
                          {noteCount > 0 && <span className="text-[10px] text-indigo-400 font-semibold">💬 {noteCount}</span>}
                          {interview.punteggio && <span className="flex items-center gap-0.5 text-[10px] text-amber-500 font-bold"><Star className="w-2.5 h-2.5" />{interview.punteggio}</span>}
                        </div>
                        
                        {/* Pipeline progress */}
                        <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-500 ${progress >= 80 ? 'bg-emerald-500' : progress >= 50 ? 'bg-blue-500' : progress >= 25 ? 'bg-amber-500' : 'bg-slate-300'}`} style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )})}

                {(grouped[stato.key] || []).length === 0 && (
                  <div className="text-center py-6 text-xs text-slate-300 italic">
                    Nessun candidato
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        </div>
      )}

      {/* Detail panel */}
      {renderDetailPanel()}

      {/* Interview form modal */}
      {/* Interview form modal (Advanced AI-Powered Glassmorphism) */}
      {showForm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowForm(false)}>
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col border border-white/20 animate-modal-in overflow-hidden relative" onClick={e => e.stopPropagation()}>
            <div className="absolute top-0 inset-x-0 h-40 bg-gradient-to-br from-indigo-600 via-purple-600 to-emerald-600 opacity-10" />
            
            <div className="px-6 py-5 border-b border-slate-200/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 z-10 relative bg-white/50 backdrop-blur-md">
              <div>
                <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-700 to-purple-700">
                  {editingInterview ? 'Modifica Profilo Colloquio' : 'Nuovo Colloquio Professionista'}
                </h3>
                <p className="text-sm text-slate-500 font-medium">Compila i dati manualmente o usa l'intelligenza artificiale per l'auto-compilazione.</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 rounded-xl text-white text-sm font-bold cursor-pointer transition-all shadow-md hover:shadow-lg shadow-emerald-500/20">
                  {ocrProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {ocrProcessing ? ocrProgress : 'A.I. Auto-Compila da CV'}
                  <input type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.txt" multiple onChange={e => { const fs = e.target.files; if (fs && fs.length > 0) handleOCRForm(fs); e.target.value = '' }} disabled={ocrProcessing} />
                </label>
                <button onClick={() => setShowForm(false)} className="p-2 hover:bg-slate-200/50 rounded-xl transition-colors"><X className="w-6 h-6 text-slate-500" /></button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 z-10 space-y-8 bg-gradient-to-b from-transparent to-slate-50/50">
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Colonna SX */}
                <div className="space-y-6">
                  <FormSection title="Anagrafica & Contatti" icon={<User className="w-4 h-4 text-indigo-500" />} color="indigo">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField label="Nome *" value={formData.nome || ''} onChange={v => setFormData({ ...formData, nome: v })} />
                      <FormField label="Cognome *" value={formData.cognome || ''} onChange={v => setFormData({ ...formData, cognome: v })} />
                      <FormField label="Codice Fiscale" value={formData.codice_fiscale || ''} onChange={v => setFormData({ ...formData, codice_fiscale: v?.toUpperCase() })} />
                      <FormField label="Telefono" value={formData.telefono || ''} onChange={v => setFormData({ ...formData, telefono: v })} />
                      <div className="col-span-2">
                        <FormField label="Email" value={formData.email || ''} onChange={v => setFormData({ ...formData, email: v })} type="email" />
                      </div>
                    </div>
                  </FormSection>

                  <FormSection title="Stato Pipeline" icon={<Briefcase className="w-4 h-4 text-amber-500" />} color="amber">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-wider">Fase Corrente</label>
                        <select value={formData.stato || 'in_attesa'} onChange={e => setFormData({ ...formData, stato: e.target.value })} className="w-full px-3.5 py-3 border border-slate-200/80 rounded-xl text-sm font-bold text-slate-800 bg-white shadow-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-400/20 outline-none transition-all">
                          {STATI.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                      </div>
                      <FormField label="Punteggio Globale (0-10)" value={formData.punteggio?.toString() || ''} onChange={v => setFormData({ ...formData, punteggio: v ? Number(v) : undefined })} type="number" />
                    </div>
                  </FormSection>

                  <FormSection title="Dettagli Colloquio" icon={<Calendar className="w-4 h-4 text-emerald-500" />} color="emerald">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField label="Data Fissata" value={formData.data_colloquio || ''} onChange={v => setFormData({ ...formData, data_colloquio: v })} type="date" />
                      <FormField label="Data Follow-Up" value={formData.data_follow_up || ''} onChange={v => setFormData({ ...formData, data_follow_up: v })} type="date" />
                      <div className="col-span-2">
                        <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-wider">Tipo Contratto Proposto</label>
                        <select value={formData.tipo_contratto || ''} onChange={e => setFormData({ ...formData, tipo_contratto: e.target.value })} className="w-full px-3.5 py-3 border border-slate-200/80 rounded-xl text-sm font-bold text-slate-800 bg-white shadow-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-400/20 outline-none transition-all">
                          <option value="">Non specificato</option>
                          <option value="piva">Partita IVA</option>
                          <option value="ritenuta">Ritenuta d'Acconto</option>
                        </select>
                      </div>
                    </div>
                  </FormSection>
                </div>

                {/* Colonna DX */}
                <div className="space-y-6">
                  <FormSection title="Dati Professionali & Contrattuali" icon={<Shield className="w-4 h-4 text-purple-500" />} color="purple">
                     <div className="grid grid-cols-2 gap-4">
                        <FormField label="Partita IVA *" value={formData.partita_iva || ''} onChange={v => setFormData({ ...formData, partita_iva: v })} />
                        <FormField label="IBAN" value={formData.iban || ''} onChange={v => setFormData({ ...formData, iban: v?.toUpperCase() })} />
                        <FormField label="PEC (Contratto)" value={formData.pec_contratto || ''} onChange={v => setFormData({ ...formData, pec_contratto: v })} type="email" />
                        <FormField label="Albo & Provincia (OPI)" value={formData.albo_iscrizione || ''} onChange={v => setFormData({ ...formData, albo_iscrizione: v })} />
                        <FormField label="N° Iscrizione Albo" value={formData.albo_numero || ''} onChange={v => setFormData({ ...formData, albo_numero: v })} />
                        <FormField label="Data Abilitazione" value={formData.data_abilitazione || ''} onChange={v => setFormData({ ...formData, data_abilitazione: v })} type="date" />
                        <FormField label="Data Iscrizione Albo" value={formData.data_iscrizione_albo || ''} onChange={v => setFormData({ ...formData, data_iscrizione_albo: v })} type="date" />
                        <div className="col-span-2 border-t border-slate-100 pt-4 mt-2">
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-3">🛡️ Assicurazione Professionale</div>
                          <div className="grid grid-cols-2 gap-4">
                            <FormField label="Compagnia Assicurativa" value={formData.polizza_compagnia || ''} onChange={v => setFormData({ ...formData, polizza_compagnia: v })} />
                            <FormField label="N° Polizza" value={formData.polizza_numero || ''} onChange={v => setFormData({ ...formData, polizza_numero: v })} />
                            <FormField label="Massimale (€)" value={formData.polizza_massimale || ''} onChange={v => setFormData({ ...formData, polizza_massimale: v })} />
                            <FormField label="Scadenza Polizza" value={formData.polizza_scadenza || ''} onChange={v => setFormData({ ...formData, polizza_scadenza: v })} type="date" />
                          </div>
                        </div>
                     </div>
                  </FormSection>

                  <FormSection title="Località Operativa" icon={<MapPin className="w-4 h-4 text-sky-500" />} color="sky">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-wider">Regione</label>
                        <select value={formData.regione || ''} onChange={e => setFormData({ ...formData, regione: e.target.value, provincia: '' })} className="w-full px-3.5 py-3 border border-slate-200/80 rounded-xl text-sm bg-white shadow-sm focus:border-sky-400 focus:ring-4 focus:ring-sky-400/20 outline-none transition-all">
                          <option value="">Seleziona</option>
                          {Object.keys(regionsData).sort().map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-wider">Provincia</label>
                        <select value={formData.provincia || ''} onChange={e => setFormData({ ...formData, provincia: e.target.value })} disabled={!formData.regione} className="w-full px-3.5 py-3 border border-slate-200/80 rounded-xl text-sm bg-white shadow-sm focus:border-sky-400 focus:ring-4 focus:ring-sky-400/20 outline-none transition-all disabled:opacity-50">
                          <option value="">Seleziona</option>
                          {formData.regione && (regionsData[formData.regione] || []).map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <FormField label="Indirizzo Domicilio" value={formData.indirizzo || ''} onChange={v => setFormData({ ...formData, indirizzo: v })} />
                      </div>
                      <div className="col-span-2">
                        <FormField label="Indirizzo Residenza (se diverso dal domicilio)" value={formData.residenza_contratto || ''} onChange={v => setFormData({ ...formData, residenza_contratto: v })} />
                      </div>
                      <FormField label="Zone Coperte" value={formData.zone_coperte || ''} onChange={v => setFormData({ ...formData, zone_coperte: v })} />
                      <FormField label="Raggio Km" value={formData.km_spostamento || ''} onChange={v => setFormData({ ...formData, km_spostamento: v })} />
                      <FormField label="Disponibilità" value={(formData as any).disponibilita || ''} onChange={v => setFormData({ ...formData, disponibilita: v } as any)} />
                      <FormField label="Fasce Orarie" value={(formData as any).fasce_orarie || ''} onChange={v => setFormData({ ...formData, fasce_orarie: v } as any)} />
                    </div>
                  </FormSection>
                </div>
              </div>

              {/* Note Full Width */}
              <FormSection title="Note Descrittive" icon={<FileText className="w-4 h-4 text-slate-500" />} color="slate">
                  <textarea
                    value={formData.note || ''}
                    onChange={e => setFormData({ ...formData, note: e.target.value })}
                    rows={4}
                    placeholder="Aggiungi impressioni, dettagli sul colloquio o richieste particolari..."
                    className="w-full px-4 py-3 border border-slate-200/80 rounded-xl text-sm bg-white shadow-sm focus:border-slate-400 focus:ring-4 focus:ring-slate-400/20 outline-none transition-all resize-none"
                  />
                  <div className="mt-4">
                     <FormField label="Tag (separati da virgola)" value={formData.tags || ''} onChange={v => setFormData({ ...formData, tags: v })} />
                  </div>
              </FormSection>

            </div>
            
            <div className="p-6 bg-slate-50/80 border-t border-slate-200/50 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-4 z-10 backdrop-blur-md">
              <p className="text-xs text-slate-400 max-w-sm">I dati salvati verranno inclusi nella scheda candidato e potranno sempre essere aggiornati.</p>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <button onClick={() => setShowForm(false)} className="flex-1 sm:flex-none px-6 py-3 text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm">
                  Annulla
                </button>
                <button onClick={handleSave} disabled={saving} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-8 py-3 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-md hover:shadow-lg shadow-indigo-500/30 disabled:opacity-50 transition-all hover:-translate-y-0.5">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {saving ? 'Salvataggio...' : editingInterview ? 'Aggiorna Profilo' : 'Crea Candidato'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function FormSection({ title, icon, color, children }: { title: string, icon: React.ReactNode, color: string, children: React.ReactNode }) {
  const colorMap: Record<string, string> = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    sky: 'bg-sky-50 text-sky-700 border-sky-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-100'
  }
  
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm">
      <div className={`px-4 py-3 flex items-center gap-2 border-b ${colorMap[color] || colorMap.slate}`}>
        {icon}
        <h4 className="text-xs font-black uppercase tracking-wider">{title}</h4>
      </div>
      <div className="p-5">
        {children}
      </div>
    </div>
  )
}

function FormField({ label, value, onChange, type = 'text', required = false }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean
}) {
  return (
    <div>
      <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-wider">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3.5 py-3 border border-slate-200/80 rounded-xl text-sm bg-white shadow-sm focus:border-indigo-400 focus:ring-4 focus:ring-indigo-400/20 outline-none transition-all placeholder-slate-300"
        placeholder={`Inserisci ${label.toLowerCase().replace('*', '').trim()}`}
      />
    </div>
  )
}
